/**
 * iter 73.12 (Dilly) — PUBLIC migration status landing page.
 *
 * Mounted at `/migration-status/:jobId`. UNauthenticated — the users
 * landing here are the "displaced" Org B members whose sessions were
 * killed at step 1 of the merge. They can't sign in (their org is
 * about to be sealed) but they still need a branded, human-readable
 * "your data is being moved to X" experience.
 *
 * Renders:
 *   • Target org logo + name (from public branding endpoint)
 *   • Source org name ("You were a member of {Source} which is being
 *     merged into…")
 *   • Live progress bar coloured by the target org's brand_primary_color
 *     (falls back to blue if the target hasn't set one)
 *   • Current table name being re-keyed + last error (if any)
 *   • Auto-refresh every 4s while the job is non-terminal
 *   • When status === completed → prompts the user to sign in at the
 *     new org (the target's login page — we don't know that URL, so
 *     we just show "Please sign in with your existing credentials" and
 *     link to `/login`).
 *
 * Purely presentational — no writes, no user state, no session cookies.
 */
import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CheckCircle, Hourglass, WarningCircle, ArrowsClockwise, SignIn } from "@phosphor-icons/react";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const TERMINAL = new Set(["completed", "failed", "aborted"]);

const STATUS_LABELS = {
  pending_review: "Waiting for platform verification…",
  approved:       "Approved — starting merge…",
  pre_flight:     "Running pre-flight checks…",
  in_progress:    "Merging data — this can take a few minutes.",
  paused:         "Merge paused. Support has been notified.",
  completed:      "Merge complete. Please sign in.",
  failed:         "Merge failed. Support has been notified.",
  aborted:        "Merge cancelled.",
};

export default function PublicMigrationStatus() {
  const { jobId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const timerRef = useRef(null);

  const fetchStatus = async () => {
    try {
      const res = await fetch(`${BACKEND}/api/public/migration-status/${jobId}`);
      if (!res.ok) {
        if (res.status === 404) throw new Error("Migration job not found. Please double-check the link you were sent.");
        throw new Error(`Server returned ${res.status}. Please refresh in a moment.`);
      }
      const json = await res.json();
      setData(json);
      setError(null);
    } catch (e) {
      setError(e.message || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!jobId) return;
    fetchStatus();
    // Poll every 4 s while non-terminal; setTimeout so we don't stack.
    const schedule = () => {
      timerRef.current = setTimeout(async () => {
        await fetchStatus();
        if (!TERMINAL.has(data?.status)) schedule();
      }, 4000);
    };
    schedule();
    return () => clearTimeout(timerRef.current);
  }, [jobId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/20">
        <ArrowsClockwise size={24} className="animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/20 p-4">
        <div className="max-w-md w-full surface p-6 text-center" data-testid="public-migration-error">
          <WarningCircle size={40} weight="duotone" className="text-destructive mx-auto mb-3" />
          <h1 className="text-lg font-semibold mb-2">Unable to load merge status</h1>
          <p className="text-sm text-muted-foreground">{error}</p>
          <Link to="/login" className="btn btn-outline mt-4 inline-flex items-center gap-2">
            <SignIn size={14} /> Return to sign-in
          </Link>
        </div>
      </div>
    );
  }

  const { status, progress, target_org, source_org } = data;
  const primary = target_org.primary_color || "#3b82f6";
  const accent = target_org.accent_color || primary;
  const terminal = TERMINAL.has(status);
  const label = STATUS_LABELS[status] || status;
  const StatusIcon = status === "completed" ? CheckCircle : (status === "failed" || status === "aborted") ? WarningCircle : Hourglass;
  const statusColorClass = status === "completed"
    ? "text-green-600 dark:text-green-400"
    : (status === "failed" || status === "aborted")
      ? "text-destructive"
      : "text-amber-600 dark:text-amber-400";

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4 bg-muted/20"
      data-testid="public-migration-status"
    >
      <div
        className="max-w-lg w-full rounded-lg border-2 bg-card shadow-sm p-8"
        style={{ borderColor: `${primary}33` }}   /* 20% opacity accent border */
      >
        {/* Target org header */}
        <div className="flex items-center gap-4 mb-6">
          {target_org.logo_url ? (
            <img
              src={target_org.logo_url}
              alt={target_org.name}
              className="w-16 h-16 rounded object-contain bg-muted"
              data-testid="public-migration-target-logo"
            />
          ) : (
            <div
              className="w-16 h-16 rounded flex items-center justify-center text-2xl font-bold text-white"
              style={{ background: primary }}
              data-testid="public-migration-target-initial"
            >
              {(target_org.name || "?").slice(0, 1).toUpperCase()}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="text-[10px] mono uppercase text-muted-foreground tracking-wide">
              You are being merged into
            </div>
            <div className="text-xl font-semibold truncate" data-testid="public-migration-target-name">
              {target_org.name}
            </div>
          </div>
        </div>

        {/* Source org caption */}
        <div className="text-sm text-muted-foreground mb-6">
          <strong className="text-foreground">{source_org.name}</strong> is being consolidated into{" "}
          <strong className="text-foreground">{target_org.name}</strong>. Your account and data
          will be preserved and available at the new organization once the merge finishes.
        </div>

        {/* Status pill */}
        <div className={`flex items-center gap-2 text-sm mb-3 ${statusColorClass}`}>
          <StatusIcon
            size={18}
            weight="duotone"
            className={status === "in_progress" ? "animate-pulse" : ""}
          />
          <span className="font-medium" data-testid="public-migration-status-label">{label}</span>
        </div>

        {/* Progress bar */}
        <div
          className="h-2 rounded-full bg-muted overflow-hidden mb-2"
          data-testid="public-migration-progress-bar"
        >
          <div
            className="h-full transition-all duration-500 ease-out"
            style={{
              width: `${progress.percent}%`,
              background: `linear-gradient(90deg, ${primary}, ${accent})`,
            }}
          />
        </div>
        <div className="flex items-center justify-between text-[11px] text-muted-foreground mono">
          <span data-testid="public-migration-percent">{progress.percent}% complete</span>
          <span>
            {progress.batches_completed} / {progress.total_batches || "?"} batches
            {progress.current_table && !terminal && (
              <> · <span className="text-foreground">{progress.current_table}</span></>
            )}
          </span>
        </div>

        {progress.last_error && !terminal && (
          <div
            className="mt-4 p-3 rounded border border-amber-500/40 bg-amber-500/5 text-xs text-amber-800 dark:text-amber-200"
            data-testid="public-migration-last-error"
          >
            <strong>Latest note from the engine:</strong> {progress.last_error}
          </div>
        )}

        {/* Post-completion CTA */}
        {status === "completed" && (
          <div className="mt-6 pt-6 border-t border-border">
            <p className="text-sm text-muted-foreground mb-3">
              The merge finished successfully. Sign in with your existing credentials — you&apos;ll
              now see the {target_org.name} workspace.
            </p>
            <Link
              to="/login"
              className="btn w-full text-white hover:opacity-90 transition-opacity"
              style={{ background: primary }}
              data-testid="public-migration-signin-btn"
            >
              <SignIn size={16} className="mr-2" /> Sign in to {target_org.name}
            </Link>
          </div>
        )}

        {(status === "failed" || status === "aborted") && (
          <div className="mt-6 pt-6 border-t border-border">
            <p className="text-sm text-muted-foreground">
              The merge did not finish. Your original {source_org.name} account is still active —
              please sign in there normally. Our support team has been notified.
            </p>
            <Link to="/login" className="btn btn-outline w-full mt-3">
              <SignIn size={16} className="mr-2" /> Return to sign-in
            </Link>
          </div>
        )}

        {/* Auto-refresh indicator */}
        {!terminal && (
          <div className="mt-6 flex items-center justify-center gap-1 text-[10px] text-muted-foreground">
            <ArrowsClockwise size={10} className="animate-spin" />
            <span>Auto-refreshing every 4 seconds</span>
          </div>
        )}
      </div>
    </div>
  );
}
